import { useObserver } from 'mobx-react';
import { CallNotificationListener, InCall } from '@streem/sdk-react';
import { RoomId } from '@streem/domain-id';
import { CallNotification } from './call_notification';
import { preferredFallbackCodecsInOrder } from '../../util/constants';
import { useInCallListener } from '../../hooks/use_in_call_listener';
import { useCallback } from 'react';

export function InCallListener(): JSX.Element {
    const {
        companySettingsStore,
        history,
        currentCallSourceStore,
        authStore,
        sdkStore,
        noteTemplatesStore,
        handleAnswerCallError,
        handleAnswerCallCanceled,
        getRoomCallLogHistory,
        companyStore,
        groupStore,
        displaySurveys,
        hoursOfOperation,
        onAnswerCallback,
        isVideoProviderVerboseLoggingEnabled,
    } = useInCallListener();

    const onEnd = useCallback(
        async (roomId: RoomId) => {
            currentCallSourceStore.clearGroupReservationCallSource();

            if (!companySettingsStore.roomOutcomeFeatureEnabled) {
                await displaySurveys(authStore.userId, roomId.toString());
            }
            history.push(`/mycalls/${roomId}`);
        },
        [
            authStore.userId,
            companySettingsStore.roomOutcomeFeatureEnabled,
            currentCallSourceStore,
            displaySurveys,
            history,
        ],
    );

    return useObserver(() => {
        const {
            gpsEnabled,
            webAREnabled: arEnabled,
            faceToFaceModeEnabled,
            screenshareEnabled,
            expertAvailabilityEnabled,
            anonymousShareEnabled,
            allowPauseResumeRecording,
            chimeLowBandwidthOptimizationEnabled,
            preferredCodecs: preferredVideoCodecs,
            roomNoteCharLimit,
            streemshotNoteCharLimit,
            editorNotesEnabled,
            chimeNetworkAdaptationEnabled,
        } = companySettingsStore;

        const { groupReservationCallSource, callRequeueEnabledForCurrentSource } =
            currentCallSourceStore;
        const brandSettings = {
            brandName: groupReservationCallSource?.name,
            brandLogoUrl: groupReservationCallSource?.logoUrl,
            customTheme: groupReservationCallSource?.customTheme,
        };

        if (authStore.isAdmin || authStore.isAgent) {
            return (
                <>
                    {!sdkStore.roomSession?.current && sdkStore.userSession.current && (
                        <CallNotificationListener
                            userSession={sdkStore.userSession.current}
                            callConfig={{
                                faceToFaceModeEnabled,
                                chimeLowBandwidthOptimizationEnabled,
                                preferredVideoCodecs:
                                    preferredVideoCodecs ?? preferredFallbackCodecsInOrder,
                                showReservationCallDetailYn:
                                    companySettingsStore.ahsVirtualExpertEnabled,
                                brandSettings,
                                noteTemplates: noteTemplatesStore.noteTemplates,
                                isVideoProviderVerboseLoggingEnabled,
                                chimeNetworkAdaptationEnabled,
                            }}
                            SystemNotificationComponent={CallNotification}
                            handleAnswerCallError={handleAnswerCallError}
                            handleAnswerCallCanceled={handleAnswerCallCanceled}
                            onAnswerCallback={onAnswerCallback}
                        />
                    )}
                    {sdkStore.roomSession?.current && (
                        <InCall
                            getRoomCallLogHistory={getRoomCallLogHistory}
                            toolSettings={{
                                gpsEnabled,
                                arEnabled,
                                screenshareEnabled,
                                faceToFaceModeEnabled,
                                expertAvailabilityEnabled,
                                anonymousShareEnabled,
                                allowPauseResumeRecording,
                                roomNoteCharLimit,
                                streemshotNoteCharLimit,
                                callRequeueEnabled: callRequeueEnabledForCurrentSource,
                                editorNotesEnabled,
                            }}
                            companyInfo={{
                                companyName: companyStore.result.name,
                                companyCode: companyStore.result.code,
                            }}
                            roomSession={sdkStore.roomSession.current}
                            userSession={sdkStore.userSession.current}
                            groups={groupStore.results}
                            reservation={sdkStore.groupReservation.current}
                            callSource={{
                                callSource: currentCallSourceStore.groupReservationCallSource,
                                callRequeueEnabledForSourceYn:
                                    currentCallSourceStore.callRequeueEnabledForCurrentSource,
                            }}
                            hoursOfOperation={hoursOfOperation}
                            onEnd={onEnd}
                        />
                    )}
                </>
            );
        } else {
            return <></>;
        }
    });
}
